import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Select2, Select2Data } from 'ng-select2-component';
import Swal from 'sweetalert2';
import { ChatService, getChatErrorDetail } from '../../../services/chat.service';
import { ChatRealtimeService } from '../../../services/chat-realtime.service';
import { Helpers } from '../../../helpers/helpers';
import { ChatUserSelectDTO } from '../../../models/chat-user-select-dto';

@Component({
  selector: 'app-chat-manage-members-modal',
  standalone: true,
  imports: [FormsModule, Select2],
  templateUrl: './chat-manage-members-modal.component.html',
  styleUrl: './chat-manage-members-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatManageMembersModalComponent implements OnInit {
  @Input() idChat!: string;
  @Input() isAdmin = false;

  activeModal = inject(NgbActiveModal);
  private chatService = inject(ChatService);
  chatRealtimeService = inject(ChatRealtimeService);
  private helpers = inject(Helpers);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  members: ChatUserSelectDTO[] = [];
  userSelectData: Select2Data = [];
  selectedUserIdsToAdd: string[] = [];
  includeHistory = true;
  adding = false;
  private changed = false;

  get adminName(): string | undefined {
    return this.members.find(m => m.IsAdmin)?.Name;
  }

  ngOnInit(): void {
    this.loadMembers();
  }

  private loadMembers(): void {
    this.chatService.getRoomMembers(this.idChat).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: members => {
        this.members = members;
        this.chatRealtimeService.seedOnlineStatus(members);
        if (this.isAdmin) {
          this.loadSelectableUsers();
        } else {
          this.cdr.detectChanges();
        }
      },
      error: err => this.helpers.showError('Error al recuperar los miembros de la sala', getChatErrorDetail(err))
    });
  }

  private loadSelectableUsers(): void {
    this.chatService.getSelectableUsers().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: users => {
        this.chatRealtimeService.seedOnlineStatus(users);
        const memberIds = new Set(this.members.map(m => m.IdUser));
        this.userSelectData = users
          .filter(u => !memberIds.has(u.IdUser))
          .map(u => ({ value: u.IdUser, label: `${u.IsOnline ? '🟢' : '🔴'} ${u.Role ? `${u.Role} - ${u.Name}` : u.Name}` }));
        this.cdr.detectChanges();
      },
      error: err => this.helpers.showError('Error al recuperar los usuarios de la instalación', getChatErrorDetail(err))
    });
  }

  addSelected(): void {
    if (this.selectedUserIdsToAdd.length === 0 || this.adding) return;

    this.adding = true;
    this.chatService.addUsers(this.idChat, this.selectedUserIdsToAdd, this.includeHistory).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.adding = false;
        this.changed = true;
        this.selectedUserIdsToAdd = [];
        this.includeHistory = true;
        this.loadMembers();
      },
      error: err => {
        this.adding = false;
        this.helpers.showError('Error al añadir los usuarios a la sala', getChatErrorDetail(err));
        this.cdr.detectChanges();
      }
    });
  }

  removeMember(member: ChatUserSelectDTO): void {
    Swal.fire({
      title: `¿Quitar a ${member.Name} de la sala?`,
      icon: 'warning',
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'Quitar <i class="fa-solid fa-user-xmark"></i>',
      cancelButtonText: 'Cancelar <i class="fa-solid fa-xmark"></i>',
      reverseButtons: true,
      focusCancel: true,
      customClass: { confirmButton: 'btn btn-danger me-2', cancelButton: 'btn btn-outline-secondary' },
      buttonsStyling: false
    }).then(result => {
      if (!result.isConfirmed) return;

      this.chatService.removeUsers(this.idChat, [member.IdUser]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.changed = true;
          this.loadMembers();
        },
        error: err => this.helpers.showError('Error al quitar el usuario de la sala', getChatErrorDetail(err))
      });
    });
  }

  close(): void {
    this.activeModal.close(this.changed);
  }
}
